import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { StatusBadge } from '@/shared/components/ui/StatusBadge';
import { formatStatusLabel, onboardingStageToBadge } from '@/shared/utils/statusBadge';
import { OnboardingStage, VerificationStatus } from '@/types';
import type { AdminOnboardingItem } from '@/features/admin/api/admin.api';
import { onboardingRowName } from '@/features/admin/constants/springVerifyExport';
import {
  LEGACY_RECORD_REASON,
  daysSince,
  formatSentOn,
  formatWaitingSince,
  STAGE_LABEL,
  WAITING_ON_LABEL,
} from '@/features/admin/constants/onboardingStages';
import { GUIDE_EXPERIENCE_LABEL } from '@/features/guide-profile/constants';

/**
 * The pipeline table's columns, declared per stage.
 *
 * Header and cell are defined together so the two cannot drift, and a tab that
 * needs a different shape is one entry in `STAGE_COLUMN_IDS` rather than a
 * scatter of conditionals through the table body.
 */

export type OnboardingColumnId =
  | 'guide'
  | 'contact'
  | 'location'
  | 'experience'
  | 'waitingOn'
  | 'waiting'
  | 'sentOn'
  | 'stage'
  | 'actions';

export interface OnboardingCellContext {
  onOpen: (row: AdminOnboardingItem) => void;
  renderActions: (row: AdminOnboardingItem) => ReactNode;
}

export interface OnboardingColumn {
  id: OnboardingColumnId;
  header: string;
  align?: 'left' | 'right';
  render: (row: AdminOnboardingItem, ctx: OnboardingCellContext) => ReactNode;
}

/** Past this, the vendor has been sitting on the check long enough to chase. */
const STALE_AFTER_DAYS = 7;

const COLUMNS: Record<OnboardingColumnId, OnboardingColumn> = {
  guide: {
    id: 'guide',
    header: 'Guide',
    render: (row, ctx) => (
      <>
        {/* The name carries the link to the full record, so the tabs that drop
            the View button still have a way in without spending a column. */}
        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={() => ctx.onOpen(row)}
          sx={{
            p: 0,
            border: 0,
            background: 'none',
            cursor: 'pointer',
            textAlign: 'left',
            font: 'inherit',
          }}
        >
          <Typography variant="body2" fontWeight={600} color="primary.main">
            {onboardingRowName(row)}
          </Typography>
        </Link>
        {row.verificationReference && (
          <Typography variant="caption" color="text.secondary" display="block">
            Ref {row.verificationReference}
          </Typography>
        )}
      </>
    ),
  },

  contact: {
    id: 'contact',
    header: 'Contact',
    render: (row) => (
      <>
        <Typography variant="body2">{row.user?.phone ?? '—'}</Typography>
        <Typography variant="caption" color="text.secondary">
          {row.user?.email ?? '—'}
        </Typography>
      </>
    ),
  },

  location: {
    id: 'location',
    header: 'Location',
    render: (row) => (
      <Typography variant="body2">
        {[row.city, row.state].filter(Boolean).join(', ') || '—'}
      </Typography>
    ),
  },

  experience: {
    id: 'experience',
    header: 'Experience',
    render: (row) => (
      <>
        <Typography variant="body2">
          {row.guideExperienceRange ? GUIDE_EXPERIENCE_LABEL[row.guideExperienceRange] : '—'}
        </Typography>
        {/* Absent on applications made before the question existed. */}
        {row.currentlyWorkingAsGuide != null && (
          <Typography variant="caption" color="text.secondary">
            {row.currentlyWorkingAsGuide ? 'Currently guiding' : 'Not currently guiding'}
          </Typography>
        )}
      </>
    ),
  },

  waitingOn: {
    id: 'waitingOn',
    header: 'Waiting on',
    render: (row) => (
      <Typography
        variant="body2"
        fontWeight={row.waitingOn === 'admin' ? 700 : 400}
        color={row.waitingOn === 'admin' ? 'text.primary' : 'text.secondary'}
      >
        {WAITING_ON_LABEL[row.waitingOn]}
      </Typography>
    ),
  },

  waiting: {
    id: 'waiting',
    header: 'Waiting',
    render: (row) => (
      <Typography variant="body2" color="text.secondary">
        {formatWaitingSince(row.waitingSince)}
      </Typography>
    ),
  },

  sentOn: {
    id: 'sentOn',
    header: 'Sent to SpringVerify',
    render: (row) => <SentOnCell row={row} />,
  },

  stage: {
    id: 'stage',
    header: 'Stage',
    render: (row) => (
      <StatusBadge
        status={onboardingStageToBadge(row.stage)}
        label={formatStatusLabel(STAGE_LABEL[row.stage] ?? row.stage)}
      />
    ),
  },

  actions: {
    id: 'actions',
    header: 'Actions',
    align: 'right',
    render: (row, ctx) => ctx.renderActions(row),
  },
};

const DEFAULT_COLUMN_IDS: readonly OnboardingColumnId[] = [
  'guide',
  'contact',
  'location',
  'experience',
  'waitingOn',
  'waiting',
  'stage',
  'actions',
];

/**
 * Background Check trades "Waiting on" for "Sent to SpringVerify": in that tab
 * the only question is whether this applicant's details have gone to the vendor
 * yet, which "Waiting on" only answered obliquely.
 */
const STAGE_COLUMN_IDS: Partial<Record<OnboardingStage, readonly OnboardingColumnId[]>> = {
  [OnboardingStage.BACKGROUND_CHECK]: [
    'guide',
    'contact',
    'location',
    'experience',
    'sentOn',
    'stage',
    'actions',
  ],
};

export function columnsForStage(stage: OnboardingStage): OnboardingColumn[] {
  return (STAGE_COLUMN_IDS[stage] ?? DEFAULT_COLUMN_IDS).map((id) => COLUMNS[id]);
}

/** Whether this applicant's details are still waiting to go to the vendor. */
function SentOnCell({ row }: { row: AdminOnboardingItem }) {
  const status = row.lifecycle.verificationStatus;

  if (status === VerificationStatus.PENDING) {
    return (
      <Box>
        {/* These are exactly the rows the next export picks up, so they are the
            loud ones. */}
        <Typography variant="body2" fontWeight={600} color="warning.main">
          Not sent
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Applied {formatWaitingSince(row.createdAt).toLowerCase()}
        </Typography>
      </Box>
    );
  }

  if (status === VerificationStatus.IN_PROGRESS) {
    const days = daysSince(row.verificationSentAt);

    return (
      <Box>
        <Typography variant="body2">
          {row.verificationSentAt
            ? dayjs(row.verificationSentAt).format('D MMM YYYY')
            : 'Sent'}
        </Typography>
        <Typography
          variant="caption"
          color={days !== null && days > STALE_AFTER_DAYS ? 'error.main' : 'text.secondary'}
        >
          {row.verificationSentAt ? formatSentOn(row.verificationSentAt) : 'date unknown'}
        </Typography>
      </Box>
    );
  }

  if (status === VerificationStatus.NOT_STARTED) {
    return (
      <Tooltip title={LEGACY_RECORD_REASON}>
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      </Tooltip>
    );
  }

  return (
    <Typography variant="body2" color="text.secondary">
      —
    </Typography>
  );
}
